import React from "react";
import CashBoxPicker from "@/components/CashBoxPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Landmark, CreditCard, Plus, X, Zap } from "lucide-react";

/** Retail POS payment flow: method selection, tender details, and customer selection. */
export default function RetailPOSPaymentDialog({
  payDialog, setPayDialog, payMethod, setPayMethod, paymentBox, changeSaleCurrency,
  currentCompany, bankAccounts, bankId, setBankId, saleCurrency, convertAmount,
  total, received, setReceived, change, custId, custName, setCustId, setCustName,
  custSearch, setCustSearch, customers, createCustomer, creatingCust,
  confirmPayment, busy, t,
}) {
  return (
    <Dialog open={payDialog} onOpenChange={setPayDialog}>
      <DialogContent className="max-w-md" data-testid="pos-payment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> {t("pos_pay_settle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setPayMethod("cash")}
              className={`p-3 rounded-lg border-2 transition ${payMethod === "cash" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              data-testid="pos-pay-cash"
            >
              <Banknote className="w-6 h-6 mx-auto mb-1 text-emerald-600" />
              <div className="text-sm font-bold">{t("pos_cash")}</div>
            </button>
            <button
              onClick={() => setPayMethod("bank")}
              className={`p-3 rounded-lg border-2 transition ${payMethod === "bank" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              data-testid="pos-pay-bank"
            >
              <Landmark className="w-6 h-6 mx-auto mb-1 text-sky-600" />
              <div className="text-sm font-bold">حوالة بنكية</div>
            </button>
            <button
              onClick={() => setPayMethod("credit")}
              className={`p-3 rounded-lg border-2 transition ${payMethod === "credit" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              data-testid="pos-pay-credit"
            >
              <CreditCard className="w-6 h-6 mx-auto mb-1 text-amber-600" />
              <div className="text-sm font-bold">{t("pos_credit")}</div>
            </button>
          </div>
          {payMethod === "cash" && (
            <div className="p-2 rounded-lg border bg-emerald-50/40 border-emerald-200">
              <CashBoxPicker
                value={paymentBox}
                onChange={changeSaleCurrency}
                company={currentCompany}
                label={t("pos_money_to")}
                compact
                data-testid="pos-payment-box"
              />
            </div>
          )}
          {payMethod === "bank" && (
            <div className="p-2 rounded-lg border bg-sky-50/50 border-sky-200 space-y-1" data-testid="pos-bank-picker">
              <label className="text-xs text-sky-800 font-semibold">الحساب البنكي المستلِم (يُودَع المبلغ فيه)</label>
              {bankAccounts.length === 0 ? (
                <div className="text-xs text-muted-foreground p-2">
                  لا توجد حسابات بنكية. أضِف حساباً من صفحة «البنوك» أولاً.
                </div>
              ) : (
                <>
                  <Select value={bankId} onValueChange={setBankId}>
                    <SelectTrigger className="h-10" data-testid="pos-bank-select"><SelectValue placeholder="اختر الحساب البنكي" /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name} — {b.bank_name} ({b.currency}) · رصيد: {Number(b.balance || 0).toLocaleString("ar-EG")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const b = bankAccounts.find(x => x.id === bankId);
                    if (!b || (b.currency || "").toUpperCase() === saleCurrency) return null;
                    const converted = convertAmount(total, saleCurrency, b.currency);
                    return (
                      <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1" data-testid="pos-bank-fx-note">
                        سيتم تحويل {total.toLocaleString("ar-EG")} {saleCurrency} ≈ <b>{converted.toLocaleString("ar-EG")} {b.currency}</b> بسعر الصرف الحالي وإيداعه في الحساب.
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
          <div className="p-3 rounded-lg bg-muted/50 border">
            <div className="flex items-center justify-between text-sm mb-1"><span>{t("pos_total")}</span><b className="font-mono">{total.toLocaleString("ar-EG")}</b></div>
            {payMethod === "cash" && (
              <>
                <div className="mt-2">
                  <label className="text-xs text-muted-foreground">{t("pos_received")}</label>
                  <Input
                    type="number"
                    value={received}
                    onChange={(e) => setReceived(e.target.value)}
                    placeholder={total.toString()}
                    className="h-12 text-lg font-mono text-center mt-1"
                    autoFocus
                    data-testid="pos-received-input"
                  />
                </div>
                {received !== "" && +received >= total && (
                  <div className="mt-2 flex items-center justify-between p-2 rounded bg-emerald-100 text-emerald-800">
                    <span className="text-sm font-medium">{t("pos_change_label")}</span>
                    <b className="text-lg font-mono" data-testid="pos-change">{change.toLocaleString("ar-EG")}</b>
                  </div>
                )}
              </>
            )}
            {payMethod === "credit" && (
              <div className="mt-2 space-y-2" data-testid="pos-credit-customer">
                <div className="p-2 rounded bg-amber-50 text-amber-800 text-xs">{t("pos_credit_note")}</div>
                <div>
                  <label className="text-xs text-muted-foreground font-semibold">العميل (يُضاف المبلغ لحسابه)</label>
                  {custId ? (
                    <div className="mt-1 flex items-center justify-between p-2 rounded-lg border-2 border-primary bg-primary/5">
                      <span className="text-sm font-bold" data-testid="pos-selected-customer">{custName}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setCustId(null); setCustName(""); }} data-testid="pos-clear-customer"><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        value={custSearch}
                        onChange={(e) => setCustSearch(e.target.value)}
                        placeholder="ابحث بالاسم أو أضف عميلاً جديداً"
                        className="h-10 mt-1"
                        autoFocus
                        data-testid="pos-customer-search"
                      />
                      <div className="mt-1 max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-border/60">
                        {customers
                          .filter(c => !custSearch.trim() || (c.name || "").toLowerCase().includes(custSearch.trim().toLowerCase()))
                          .slice(0, 30)
                          .map(c => (
                            <button
                              key={c.id}
                              onClick={() => { setCustId(c.id); setCustName(c.name); setCustSearch(""); }}
                              className="w-full text-start px-3 py-2 text-sm hover:bg-accent transition flex items-center justify-between"
                              data-testid={`pos-customer-opt-${c.id}`}
                            >
                              <span className="font-medium">{c.name}</span>
                              {!!(c.balance) && <span className="text-xs text-muted-foreground font-mono">مدين: {Number(c.balance).toLocaleString("ar-EG")}</span>}
                            </button>
                          ))}
                        {customers.filter(c => !custSearch.trim() || (c.name || "").toLowerCase().includes(custSearch.trim().toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground">لا يوجد عميل مطابق</div>
                        )}
                      </div>
                      {custSearch.trim() && !customers.some(c => (c.name || "").trim().toLowerCase() === custSearch.trim().toLowerCase()) && (
                        <Button onClick={createCustomer} disabled={creatingCust} size="sm" variant="outline" className="mt-1 w-full gap-1 border-emerald-300 text-emerald-700" data-testid="pos-add-customer">
                          <Plus className="w-4 h-4" /> إضافة عميل جديد: «{custSearch.trim()}»
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setPayDialog(false)}>{t("cancel")}</Button>
          <Button onClick={confirmPayment} disabled={busy} className="min-w-[120px] gap-2" data-testid="pos-confirm-pay">
            <Zap className="w-4 h-4" />
            {busy ? t("pos_saving") : t("pos_confirm_save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
